// src/components/ChargingStation/CheckoutSection.tsx
import React, {useEffect, useState} from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import LoadingButton from '../LoadingButton';
import * as Haptics from 'expo-haptics';
import TooltipModal from '../TooltipModal';
import { tooltips, buildProcessingFeesTooltip, Tooltip_t } from '../common/tooltips/tooltips';
import RoleService from '../../services/role-service';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../navigation/stackTypes';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface CheckoutSectionProps {
  selectedChargerIsFree: boolean;
  activeCharges: any;
  order: any;
  sessionRole: string | string[] | undefined;
  scheduleSet: boolean;
  paymentData: any;
  isValidEmail: boolean;
  handleCheckoutButton: () => Promise<void>;
  handleOpenPaymentSheet: () => Promise<void>;
  showFeeTooltipVisible: boolean;
  showFeeTooltip: () => void;
  hideFeeTooltip: () => void;
}

const CheckoutSection: React.FC<CheckoutSectionProps> = ({
  selectedChargerIsFree,
  activeCharges,
  order,
  sessionRole,
  scheduleSet,
  paymentData,
  isValidEmail,
  handleCheckoutButton,
  handleOpenPaymentSheet,
  showFeeTooltipVisible,
  showFeeTooltip,
  hideFeeTooltip,
}) => {
  const navigation = useNavigation<NavigationProp>();

  const [processingFeesTooltip, setProcessingFeesTooltip] = useState<Tooltip_t>(tooltips.processingFees);

  useEffect(() => {
    if (order?.id) {
      setProcessingFeesTooltip(buildProcessingFeesTooltip(order));
    }
  }, [order?.id]);

  // Show active charge advisory if user has an active charge
  if (activeCharges && activeCharges.length > 0) {
    const activeCharge = activeCharges[0];
    return (
      <View style={styles.activeChargeAdvisory}>
        <View style={styles.advisoryHeader}>
          <MaterialIcons name="info" size={24} color="#FFB800" />
          <Text style={styles.advisoryTitle}>Active Charge in Progress</Text>
        </View>
        <Text style={styles.advisoryText}>
          You currently have an active charge at{' '}
          <Text style={styles.advisoryHighlight}>
            {activeCharge.charger?.name || 'another charger'}
          </Text>
          . You must end your current charge before starting a new one.
        </Text>
        <Pressable
          style={({ pressed }) => [
            styles.viewActiveChargeButton,
            { opacity: pressed ? 0.8 : 1 },
          ]}
          onPress={() => {
            if (Platform.OS !== 'web') {
              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
            }
            navigation.navigate('ChargingScreen', {
              order: activeCharge.order,
              scheduleObj: activeCharge.schedule,
            });
          }}
        >
          <Text style={styles.viewActiveChargeButtonText}>
            View Active Charge
          </Text>
          <MaterialIcons name="keyboard-arrow-right" size={20} color="#1A1D21" />
        </Pressable>
      </View>
    );
  }

  // Don't render checkout if charger is free or no order
  if (selectedChargerIsFree || !order) {
    return null;
  }

  return (
    <View style={styles.BgColor4} className="p-6">
      <Text className="text-base font-bold text-center text-slate-100">
        Complete checking out this charger
      </Text>
      <Text className="text-base font-normal text-center text-slate-100">
        By pressing "Check Out Charger" you agree to this charger's hourly rate
        above, plus applicable fees.{' '}
        <AntDesign
          onPress={showFeeTooltip}
          name="questioncircle"
          size={16}
          color="#fff"
          style={styles.tooltip}
        />
      </Text>
      <View style={{ marginTop: 10 }}>
        {Platform.OS === 'web' ? (
          <LoadingButton
            onPress={handleCheckoutButton}
            disabled={
              Platform.OS === 'web'
                ? !paymentData || (RoleService.parseUserRoles(sessionRole).includes('guest') && !isValidEmail)
                : false
            }
          >
            Complete Checkout
          </LoadingButton>
        ) : (
          <LoadingButton 
            onPress={async () => { 
              try { await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success); } 
              catch {} 
              await handleOpenPaymentSheet(); 
            }}
            disabled={!isValidEmail && RoleService.parseUserRoles(sessionRole).includes("guest")}
          >
            Pay and Checkout
          </LoadingButton>
        )}
      </View>

      <TooltipModal
        tooltipData={processingFeesTooltip}
        visible={showFeeTooltipVisible}
        onClose={hideFeeTooltip}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  BgColor4: {
    backgroundColor: '#003547',
  },
  tooltip: {
    marginLeft: 8,
  },
  activeChargeAdvisory: {
    backgroundColor: '#2D2A1F',
    borderRadius: 12,
    padding: 20,
    marginHorizontal: 16,
    marginVertical: 16,
    borderWidth: 1,
    borderColor: '#FFB800',
  },
  advisoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 10,
  },
  advisoryTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFB800',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  advisoryText: {
    fontSize: 14,
    color: '#FFFFFF',
    lineHeight: 20,
    marginBottom: 16,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  advisoryHighlight: {
    color: '#39BEB7',
    fontWeight: '600',
  },
  viewActiveChargeButton: {
    backgroundColor: '#39BEB7',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  viewActiveChargeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1A1D21',
    fontFamily: 'PlusJakartaSans-Regular',
  },
});

export default CheckoutSection;
